import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from '../src/hooks/useI18nNavigate';
import { useAuth, ID_TOKEN_KEY } from '@edusense/shared-auth-core';
import { exchangeAuthorizationCode } from '@edusense/shared-auth-core';
import { StatusDisplay } from '@edusense/shared-ui-kit';
import { getOAuthRedirectUri } from '../utils/url-utils';
import { isLearnSurface } from '@edusense/shared-constants';
// Split imports to avoid Rollup binding analysis issues
import { common_loading } from '@paraglide/messages';
import { common_error } from '@paraglide/messages';
import { common_success } from '@paraglide/messages';
import { actions_goHome } from '@paraglide/messages';

// --- Constants ---
const AUTH_SERVER_URL = import.meta.env.VITE_AUTH_SERVER_URL || 'http://authserver.local';
const API_SERVER_URL = import.meta.env.VITE_API_SERVER_URL || 'https://api.ugot.uk';
const USE_PROXY = true;
const CLIENT_ID = 'app-client';

const OAuth2RedirectHandler: React.FC = () => {
  console.log('[OAuth2RedirectHandler] 🚀 컴포넌트 마운트됨');
  
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const processed = useRef(false);
  const navigated = useRef(false);
  const { setAuthenticatedState, fetchUserEmail } = useAuth();
  const instanceId = useRef(`oauth-${Date.now()}`).current;
  
  console.log('[OAuth2RedirectHandler] 🔍 useAuth 결과:', {
    hasSetAuthenticatedState: !!setAuthenticatedState,
    setAuthenticatedStateType: typeof setAuthenticatedState,
    setAuthenticatedStateName: setAuthenticatedState?.name,
    instanceId
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    
    // URL 파라미터를 즉시 읽고 저장
    const initialSearch = location.search;
    
    console.log('[OAuth2RedirectHandler] 🔍 useEffect 실행됨:', {
      instanceId,
      initialSearch,
      locationHref: window.location.href,
      processed: processed.current,
      navigated: navigated.current
    });
    
    // 🔍 PKCE 파라미터 디버깅 - localStorage 상태 확인
    console.log('[OAuth2RedirectHandler] 🔍 localStorage PKCE 상태 확인:', {
      oauth2_state: localStorage.getItem('oauth2_state'),
      pkce_code_verifier: localStorage.getItem('pkce_code_verifier'), 
      oauth2_session_id: localStorage.getItem('oauth2_session_id'),
      allLocalStorageKeys: Object.keys(localStorage)
    });
    
    // 조건부 실행
    if (initialSearch && initialSearch !== '?' && initialSearch !== '' && 
        !processed.current && !navigated.current) {
      
      const searchParams = new URLSearchParams(initialSearch);
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      
      // OAuth2 콜백인 경우에만 처리
      if (code || error) {
        console.log('[OAuth2RedirectHandler] Starting OAuth2 redirect handling...', {
          instanceId,
          code: code ? `${code.substring(0, 10)}...` : 'missing',
          error
        });

        // URL에서 즉시 파라미터 제거 (중복 처리 방지)
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        url.searchParams.delete('error');
        window.history.replaceState({}, document.title, url.toString());

        const handleOAuthCallback = async () => {
          // 중복 처리 방지를 위해 먼저 체크
          if (processed.current) {
            console.warn('[OAuth2RedirectHandler] Already processed!');
            return;
          }

          // 500ms 지연을 추가하여 StrictMode의 빠른 재실행 방지
          await new Promise(resolve => setTimeout(resolve, 500));

          // 지연 후 다시 체크
          if (processed.current) {
            console.warn('[OAuth2RedirectHandler] Already processed after delay!');
            return;
          }

          processed.current = true; // 처리 시작 표시

          // OAuth 콜백 검증
          // State 검증
          const storedState = localStorage.getItem('oauth2_state');
          const codeVerifier = localStorage.getItem('pkce_code_verifier');
          const sessionId = localStorage.getItem('oauth2_session_id');
          
          console.log('[OAuth2RedirectHandler] 🔍 PKCE 검증 단계:', {
            storedState: storedState ? `${storedState.substring(0, 10)}...` : 'null',
            urlState: searchParams.get('state') ? `${searchParams.get('state')!.substring(0, 10)}...` : 'null',
            codeVerifier: codeVerifier ? `${codeVerifier.substring(0, 10)}...` : 'null',
            sessionId: sessionId ? `${sessionId.substring(0, 10)}...` : 'null',
            stateMatches: storedState === searchParams.get('state')
          });
          
          if (!storedState || storedState !== searchParams.get('state')) {
            console.error('[OAuth2RedirectHandler] ❌ State 검증 실패:', {
              storedStateExists: !!storedState,
              stateMatches: storedState === searchParams.get('state'),
              reason: !storedState ? 'Missing localStorage state' : 'State mismatch'
            });
            setErrorMsg(common_error());
            setIsLoading(false);
            return;
          }
          
          if (!codeVerifier) {
            console.error('[OAuth2RedirectHandler] ❌ PKCE Code Verifier 누락!');
            console.error('[OAuth2RedirectHandler] 이것은 OAuth 플로우 시작 시 PKCE 파라미터가 제대로 설정되지 않았음을 의미합니다.');
            setErrorMsg(common_error());
            setIsLoading(false);
            return;
          }
          
          // State에서 원본 URL 추출 (URL-safe base64 디코딩)
          let originalUrl = '/';
          try {
            // URL-safe base64 디코딩 (라이브러리 방식과 호환)
            const normalizedState = storedState
              .replace(/-/g, '+')
              .replace(/_/g, '/');
            // Padding 추가
            const paddedState = normalizedState + '='.repeat((4 - normalizedState.length % 4) % 4);
            const stateData = JSON.parse(atob(paddedState));
            // 새로운 키 이름과 기존 키 이름 모두 지원
            originalUrl = stateData.originalUrl || stateData.u || '/';
          } catch (e) {
            console.warn('[OAuth2RedirectHandler] State 파싱 실패:', e);
          }

          try {
            // 토큰 교환 및 사용자 정보 획득
            const { tokenData, user } = await exchangeAuthorizationCode(
              code!,
              codeVerifier!,
              getOAuthRedirectUri(),
              CLIENT_ID,
              { USE_PROXY, AUTH_SERVER_URL, API_SERVER_URL }
            );
            
            console.log('[OAuth2RedirectHandler] Token exchange successful:', {
              user,
              access_token: tokenData.access_token.substring(0, 20) + '...',
              refresh_token: !!tokenData.refresh_token,
              expires_in: tokenData.expires_in
            });
            
            // AuthContext 업데이트 - 인증 상태 설정
            if (!setAuthenticatedState) {
              throw new Error('setAuthenticatedState is not available');
            }
            
            // setAuthenticatedState가 실제 함수인지 확인 (스텁 함수면 실패)
            const isStubFunction = setAuthenticatedState.toString().includes('AuthProvider not initialized');
            
            if (isStubFunction) {
              console.error('[OAuth2RedirectHandler] ❌ setAuthenticatedState is stub function!');
              console.error('[OAuth2RedirectHandler] AuthProvider가 제대로 초기화되지 않았습니다');
              throw new Error('AuthProvider not properly initialized - OAuth flow cannot complete');
            }
            
            console.log('[OAuth2RedirectHandler] ✅ AuthProvider 정상 초기화 확인됨');
            
            console.log('[OAuth2RedirectHandler] 🚀 setAuthenticatedState 호출 시작:', {
              hasUser: !!user,
              userId: user?.id,
              username: user?.username,
              hasAccessToken: !!tokenData.access_token,
              accessTokenLength: tokenData.access_token?.length,
              hasRefreshToken: !!tokenData.refresh_token,
              expiresIn: tokenData.expires_in,
              tokenType: tokenData.token_type
            });
            
            // setAuthenticatedState에 ID 토큰도 전달 (6번째 파라미터)
            console.log('[OAuth2RedirectHandler] 🚀 setAuthenticatedState 호출 직전 - 파라미터 검증:', {
              user: !!user,
              userId: user?.id?.substring(0, 8),
              username: user?.username,
              accessToken: !!tokenData.access_token && tokenData.access_token.length > 10,
              refreshToken: !!tokenData.refresh_token && tokenData.refresh_token.length > 10,
              idToken: !!tokenData.id_token && tokenData.id_token.length > 10,
              expiresIn: tokenData.expires_in,
              tokenType: tokenData.token_type || 'Bearer'
            });
            
            try {
              await setAuthenticatedState(
                user,
                tokenData.access_token,
                tokenData.refresh_token,
                tokenData.expires_in,
                tokenData.token_type || 'Bearer',
                tokenData.id_token  // ID 토큰 추가!
              );
              
              console.log('[OAuth2RedirectHandler] ✅ setAuthenticatedState 호출 완료');
              // userInfo 표준 picture/name 동기화 (profileImage 포함) → localStorage에도 반영
              try {
                if (fetchUserEmail) {
                  await fetchUserEmail();
                  console.log('[OAuth2RedirectHandler] ✅ userinfo 동기화 완료 (profileImage 포함)');
                }
              } catch (e) {
                console.warn('[OAuth2RedirectHandler] userinfo 동기화 중 경고:', e);
              }
            } catch (authStateError) {
              console.error('[OAuth2RedirectHandler] ❌ setAuthenticatedState 실패:', authStateError);
              console.error('[OAuth2RedirectHandler] Error stack:', authStateError.stack);
              throw authStateError; // 에러를 다시 던져서 외부 catch에서도 잡히도록
            }
            
            // 호출 후 localStorage 즉시 확인
            setTimeout(() => {
              const storedAccessToken = localStorage.getItem('auth_token');
              const storedRefreshToken = localStorage.getItem('auth_refresh_token');
              const storedIdToken = localStorage.getItem('auth_id_token');
              
              console.log('[OAuth2RedirectHandler] 🔍 setAuthenticatedState 후 localStorage 상태:', {
                hasAccessToken: !!storedAccessToken,
                accessTokenLength: storedAccessToken?.length || 0,
                hasRefreshToken: !!storedRefreshToken,
                refreshTokenLength: storedRefreshToken?.length || 0,
                hasIdToken: !!storedIdToken,
                idTokenLength: storedIdToken?.length || 0
              });
            }, 100);
            
            // localStorage 정리
            localStorage.removeItem('oauth2_state');
            localStorage.removeItem('pkce_code_verifier');
            localStorage.removeItem('oauth2_session_id');
            
            // OAuth 성공 후 메인 페이지로 리다이렉션
            const targetUrl = '/';
            console.log('[OAuth2RedirectHandler] Login successful, navigating to:', targetUrl, '(original:', originalUrl, ')');
            navigated.current = true;
            
            // setTimeout을 사용하여 navigate가 확실히 실행되도록 함
            setTimeout(() => {
              navigate(targetUrl, { replace: true });
            }, 100);
          } catch (err) {
            console.error('[OAuth2RedirectHandler] Unexpected error:', err);
            setErrorMsg(common_error());
          } finally {
            setIsLoading(false);
          }
        };

        // 비동기 함수 실행
        timeoutId = setTimeout(() => {
          handleOAuthCallback().catch(error => {
            console.error('[OAuth2RedirectHandler] Unhandled error:', error);
            setErrorMsg(common_error());
            setIsLoading(false);
          });
        }, 100);
      } else {
        console.log('[OAuth2RedirectHandler] Not an OAuth2 callback, skipping...');
        setIsLoading(false);
      }
    } else {
      console.log('[OAuth2RedirectHandler] Skipping due to conditions:', {
        hasSearch: !!initialSearch,
        processed: processed.current,
        navigated: navigated.current
      });
      setIsLoading(false);
      
      // 쿼리 파라미터가 없고 아직 navigate하지 않았으면 메인으로 리다이렉트
      if (!initialSearch && !navigated.current) {
        console.log('[OAuth2RedirectHandler] No query parameters, redirecting to main...');
        navigated.current = true;
        setTimeout(() => navigate('/', { replace: true }), 100);
      }
    }

    // cleanup 함수 반환
    return () => {
      console.log('[OAuth2RedirectHandler] Cleanup called');
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [location.search, navigate, setAuthenticatedState]);

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column'
    }}>
      {isLoading ? (
        <StatusDisplay type="loading" message={common_loading()} />
      ) : errorMsg ? (
        <>
          <StatusDisplay type="error" message={errorMsg} />
          <button 
            onClick={() => navigate(isLearnSurface() ? '/en/learn' : '/en/feed')} 
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
          >
            {actions_goHome()}
          </button>
        </>
      ) : (
        <StatusDisplay type="success" message={common_success()} />
      )}
    </div>
  );
};

export default OAuth2RedirectHandler;
